/**
 * ч.1130: + Монобанк — «mono» (особистий токен ФОП, лише виписки) і «monocorp»
 * (рахунки юросіб: виписки і платежі на підпис).
 * ч.739: API-підключення до банків (Приват24 Автоклієнт, ПУМБ ІКБ REST) —
 * авто-виписки за розкладом (слоти робочого дня + «оновити при відкритті»)
 * + відправка збережених списань у банк на підпис.
 */
import { api } from './client';

export type BankApiBank = 'privat' | 'pumb' | 'mono' | 'monocorp';

export interface BankApiCredentials {
  token?: string | null;          // Приват24 Автоклієнт; Монобанк (особистий токен / токен компанії)
  client_id?: string | null;      // ПУМБ ІКБ (settings.txt банку)
  client_secret?: string | null;
  username?: string | null;       // логін ІКБ
  password?: string | null;
}

export interface BankApiConnection {
  id: string;
  organization_id: string;
  bank: BankApiBank;
  name: string;
  credentials_masked: Record<string, string>;
  account_ibans: string[] | null;
  enabled: boolean;
  /** ч.1305: пробувати розпізнати операції; нерозпізнане — «нерозпізнані» у списках. */
  auto_recognize: boolean;
  /** розпізнане одразу проводити */
  auto_post_recognized: boolean;
  fetch_days_back: number;
  last_fetch_at: string | null;
  last_fetch_status: string | null;
  last_error: string | null;
  next_fetch_at: string | null;
  last_ok_at: string | null;
  fail_count: number;
  created_at: string;
}

export interface BankApiConnectionCreate {
  organization_id: string;
  bank: BankApiBank;
  name: string;
  credentials: BankApiCredentials;
  account_ibans?: string[] | null;
  enabled?: boolean;
  auto_recognize?: boolean;
  auto_post_recognized?: boolean;
  fetch_days_back?: number;
}

export interface BankApiFetchLog {
  id: string;
  connection_id: string;
  started_at: string;
  finished_at: string | null;
  date_from: string | null;
  date_to: string | null;
  trigger: string;
  rows_fetched: number;
  rows_new: number;
  rows_created: number;
  rows_posted: number;
  rows_review: number;
  rows_confirmed: number;
  error: string | null;
}

export interface BankApiSendResult {
  sent: number;
  results: Array<{ id: string; number: string; ok: boolean; message: string }>;
}

export const bankApi = {
  list: (organizationId: string) =>
    api.get<BankApiConnection[]>('/bank-api/connections', { params: { organization_id: organizationId } })
      .then((r) => r.data),
  create: (p: BankApiConnectionCreate) =>
    api.post<BankApiConnection>('/bank-api/connections', p).then((r) => r.data),
  update: (id: string, p: Partial<Omit<BankApiConnectionCreate, 'organization_id' | 'bank'>>) =>
    api.patch<BankApiConnection>(`/bank-api/connections/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`/bank-api/connections/${id}`).then(() => undefined),
  test: (id: string) =>
    api.post<{ ok: boolean; message: string; accounts: Array<Record<string, unknown>> }>(
      `/bank-api/connections/${id}/test`).then((r) => r.data),
  /** ч.929: «оновити при відкритті» — у чергу йдуть підключення організації, де
   * остання синхронізація давніша за 10 хв; повертає, скільки поставлено. */
  refresh: (organizationId: string) =>
    api.post<{ queued: number; connection_ids: string[] }>('/bank-api/refresh', { organization_id: organizationId })
      .then((r) => r.data),
  sync: (id: string, p?: { date_from?: string | null; date_to?: string | null }) =>
    api.post<BankApiFetchLog>(`/bank-api/connections/${id}/sync`, p ?? {}).then((r) => r.data),
  log: (id: string) =>
    api.get<BankApiFetchLog[]>(`/bank-api/connections/${id}/log`).then((r) => r.data),
  sendPayments: (organizationId: string, ids: string[]) =>
    api.post<BankApiSendResult>('/bank-api/send-payments', { organization_id: organizationId, ids })
      .then((r) => r.data),
};
